import React, { useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  Container,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "@studio-freight/lenis";
import LaunchIcon from "@mui/icons-material/Launch";
import { useGetProjectsQuery } from "../../../Store/api/rtk";
import { useParams } from "react-router-dom";
import ProjectDetailsSkeleton from "./ProjectDetailsSkeleton";
import SeoHelmet from "../../../Components/SeoHelmet";

gsap.registerPlugin(ScrollTrigger);

export default function ProjectDetails() {
  const { code } = useParams();
  const { data, isSuccess } = useGetProjectsQuery({ code });
  const projectData = data?.data?.d?.[0] || {};
  const containerRef = useRef(null);
  const heroImgRef = useRef(null);

  useEffect(() => {
    // If no data, skip GSAP initialization
    if (!projectData) return;

    // Initialize Lenis for smooth scrolling
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    lenis.on("scroll", ScrollTrigger.update);

    // Initialize GSAP animations
    const ctx = gsap.context(() => {
      // Fade up animation for textual elements
      gsap.fromTo(
        ".fade-up",
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1.2, stagger: 0.1, ease: "power2.out" },
      );

      // Parallax effect for the hero image
      if (heroImgRef.current) {
        gsap.to(heroImgRef.current, {
          yPercent: 12,
          ease: "none",
          scrollTrigger: {
            trigger: ".hero-container",
            start: "top top",
            end: "bottom top",
            scrub: true,
          },
        });
      }
    }, containerRef);

    // Cleanup function on unmount
    return () => {
      ctx.revert();
      lenis.destroy();
    };
  }, [projectData]);

  // Extract variables based on the exact keys from the object
  const {
    name,
    desc,
    category,
    client,
    year,
    role,
    tags,
    image_path,
    content_sections,
    links,
  } = projectData;

  // Separate hero image (index 0) and gallery images (rest of the array)
  const heroImage = image_path?.[0];
  const galleryImages = image_path?.slice(1) || [];

  if (!isSuccess) return <ProjectDetailsSkeleton />;

  // Dynamically generate keywords based on project tags and category
  const dynamicKeywords = tags?.length
    ? `${tags.join(", ")}, ${category}, پروژه ویرا, vira-tec portfolio`
    : "پروژه ویرا, نمونه کار vira-tec, مهندسی نرم‌افزار, طراحی سایت";

  return (
    <>
      {/* Dynamic SEO Helmet injected with project-specific data */}
      <SeoHelmet
        title={
          name
            ? `پروژه ${name} | نمونه‌کارهای ویرا`
            : "جزئیات پروژه | آژانس ویرا"
        }
        metaDescription={
          desc ||
          "بررسی دقیق جزئیات، تکنولوژی‌های استفاده شده و دستاوردهای این پروژه در پورتفولیوی تخصصی آژانس ویرا."
        }
        metaKeywords={dynamicKeywords}
        ogImage={heroImage ? import.meta.env.VITE_URL + heroImage : undefined}
      />

      <Box
        ref={containerRef}
        sx={{
          color: "#F5F5F7",
          minHeight: "100vh",
          pb: 15,
          "& *": { direction: "rtl" },
        }}
      >
        {/* Header and Title Section */}
        <Container maxWidth="xl" sx={{ pt: { xs: 15, md: 20 }, pb: 8 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "start", md: "flex-end" }}
            spacing={6}
          >
            <Box sx={{ maxWidth: 800 }}>
              <Box
                className="fade-up"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 4,
                  bgcolor: "rgba(255,255,255,0.03)",
                  px: 2,
                  py: 1,
                  borderRadius: "100px",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "#00ffcc",
                  }}
                />
                <Typography
                  sx={{ fontSize: 13, color: "#A0A0A5", letterSpacing: "1px" }}
                >
                  {category}
                </Typography>
              </Box>

              <Typography
                className="fade-up"
                sx={{
                  fontSize: { xs: 38, md: 60 },
                  fontWeight: 600,
                  letterSpacing: "-2px",
                  lineHeight: 1.1,
                  mb: links?.[0] ? 3 : 0,
                }}
              >
                {name}
              </Typography>

              {/* Optional: Show the first link if available */}
              {links?.[0] && (
                <Button
                  className="fade-up"
                  href={links[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  endIcon={<LaunchIcon sx={{ mr: 1 }} />}
                  sx={{
                    mt: 2,
                    color: "#00ffcc",
                    borderColor: "rgba(0, 255, 204, 0.3)",
                    borderRadius: "50px",
                    "&:hover": {
                      borderColor: "#00ffcc",
                      bgcolor: "rgba(0, 255, 204, 0.05)",
                    },
                  }}
                >
                  مشاهده پروژه
                </Button>
              )}
            </Box>
            <Box
              className="fade-up"
              sx={{ maxWidth: 500, pb: 2, width: "100%" }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  color: "#A0A0A5",
                  lineHeight: 1.9,
                  textAlign: "justify",
                  width: "100%",
                }}
              >
                {desc}
              </Typography>
            </Box>
          </Stack>
        </Container>

        {/* Hero Image Section (Using index 0 from image_path array) */}
        {heroImage && (
          <Container
            maxWidth="xl"
            className="hero-container fade-up"
            sx={{ mb: 10 }}
          >
            <Box
              sx={{
                width: "100%",
                height: { xs: "50vh", md: "75vh" },
                borderRadius: "24px",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                ref={heroImgRef}
                sx={{
                  position: "absolute",
                  top: "-10%",
                  left: 0,
                  width: "100%",
                  height: "120%",
                  backgroundImage: `url(${import.meta.env.VITE_URL + heroImage})`, // Handles both string or object with URL
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  willChange: "transform",
                }}
              />
            </Box>
          </Container>
        )}

        {/* Information Table Section */}
        <Container
          maxWidth="xl"
          sx={{ mb: { xs: 15, md: 25 } }}
          className="fade-up"
        >
          <Box
            sx={{
              borderTop: "1px solid rgba(255,255,255,0.1)",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              py: 4,
            }}
          >
            <Grid container spacing={4}>
              <Grid
                size={{ xs: 6, md: 3 }}
                sx={{
                  borderLeft: { md: "1px solid rgba(255,255,255,0.05)" },
                  px: { md: 3 },
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#86868B", mb: 1 }}>
                  مشتری
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                  {client}
                </Typography>
              </Grid>
              <Grid
                size={{ xs: 6, md: 3 }}
                sx={{
                  borderLeft: { md: "1px solid rgba(255,255,255,0.05)" },
                  px: { md: 3 },
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#86868B", mb: 1 }}>
                  سال اجرا
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                  {year}
                </Typography>
              </Grid>
              <Grid
                size={{ xs: 12, md: 3 }}
                sx={{
                  borderLeft: { md: "1px solid rgba(255,255,255,0.05)" },
                  px: { md: 3 },
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#86868B", mb: 1 }}>
                  نقش تیم
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                  {role}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} sx={{ px: { md: 3 } }}>
                <Typography sx={{ fontSize: 13, color: "#86868B", mb: 1.5 }}>
                  تکنولوژی‌ها
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {tags?.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      sx={{
                        bgcolor: "#111",
                        color: "#A0A0A5",
                        border: "1px solid #333",
                      }}
                    />
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Container>

        {/* Split-screen Section: Content & Grid Gallery */}
        <Container maxWidth="xl" sx={{ mb: 20 }}>
          <Grid container spacing={8}>
            {/* Right Column: Text Content */}
            <Grid size={{ xs: 12, lg: 4 }}>
              <Box sx={{ position: "sticky", top: "15vh" }}>
                <Stack spacing={4}>
                  {content_sections?.map((section, index) => (
                    <Box key={index}>
                      <Typography
                        sx={{
                          fontSize: 18,
                          color: "#F5F5F7",
                          fontWeight: 500,
                          mb: 1,
                        }}
                      >
                        {section?.heading}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 15,
                          color: "#A0A0A5",
                          lineHeight: 1.9,
                          textAlign: "justify",
                        }}
                        dangerouslySetInnerHTML={{ __html: section?.text }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>

            {/* Left Column: Image Gallery (Skipping index 0) */}
            <Grid size={{ xs: 12, lg: 8 }}>
              <Grid container spacing={3}>
                {galleryImages?.map((img, index) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={index}>
                    <Box
                      sx={{
                        width: "100%",
                        aspectRatio: "4/3",
                        borderRadius: "20px",
                        overflow: "hidden",
                        bgcolor: "#0A0A0A",
                        border: "1px solid rgba(255,255,255,0.05)",
                        p: 1.5,
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          borderColor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={import.meta.env.VITE_URL + img} // Handles both object (with url property) or direct string
                        alt={`Gallery image ${index + 1}`}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "12px",
                          display: "block",
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
